import React, { forwardRef, useId } from 'react';

/**
 * Input component props interface
 * Extends HTML input attributes for full compatibility
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Label text for the input */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Visual variant of the input */
  variant?: 'default' | 'filled';
  /** Size of the input */
  inputSize?: 'sm' | 'md' | 'lg';
  /** Whether the input is required */
  required?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Design system colors
 */
const COLORS = {
  greyBg: 'var(--surface-surface_2)',
  greyText: 'var(--text-low_em)',
  error: '#F04438',
  white: 'var(--surface-surface_0)',
  focus: 'var(--surface-primary_med_em)',
  border: 'var(--rare-border)',
} as const;

/**
 * Input component following design system standards
 * Supports labels, error states, icons, and various sizes
 * 
 * @example
 * ```tsx
 * <Input 
 *   label="Email" 
 *   type="email" 
 *   placeholder="Enter your email"
 *   required 
 * />
 * 
 * <Input 
 *   label="Search" 
 *   leftIcon={<SearchIcon />}
 *   variant="filled"
 *   size="lg"
 * />
 * ```
 */
const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  variant = 'default',
  inputSize = 'md',
  className,
  id,
  style,
  required = false,
  disabled = false,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const hasError = Boolean(error);
  const hasIcons = Boolean(leftIcon || rightIcon);

  const getContainerStyles = (): React.CSSProperties => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    color: 'var(--text-high_em)',
    gap: '4px',
  });

  const getLabelStyles = (): React.CSSProperties => ({
    fontSize: '13px',
    fontWeight: 500,
    color: hasError ? COLORS.error : COLORS.greyText,
    marginBottom: '2px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  });

  const getInputWrapperStyles = (): React.CSSProperties => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  });

  const getInputStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      width: '100%',
      borderRadius: '12px',
      border: hasError
        ? `1px solid ${COLORS.error}`
        : `1px solid ${COLORS.border}`,
      background: variant === 'filled' ? COLORS.greyBg : COLORS.white,
      fontSize: '15px',
      outline: 'none',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, background-color 0.15s ease-in-out',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      color: disabled ? COLORS.greyText : 'inherit',
      opacity: disabled ? 0.6 : 1,
    };

    const sizeStyles: Record<NonNullable<InputProps['inputSize']>, React.CSSProperties> = {
      sm: {
        height: '40px',
        padding: hasIcons ? '0 44px' : '0 12px',
        fontSize: '13px',
      },
      md: {
        height: '48px',
        padding: hasIcons ? '0 44px' : '0 12px',
        fontSize: '15px',
      },
      lg: {
        height: '56px',
        padding: hasIcons ? '0 48px' : '0 16px',
        fontSize: '16px',
      },
    };

    // Adjust padding for specific icon positions
    if (leftIcon && !rightIcon) {
      sizeStyles[inputSize].paddingLeft = inputSize === 'lg' ? '48px' : '44px';
      sizeStyles[inputSize].paddingRight = inputSize === 'lg' ? '16px' : '12px';
    } else if (rightIcon && !leftIcon) {
      sizeStyles[inputSize].paddingLeft = inputSize === 'lg' ? '16px' : '12px';
      sizeStyles[inputSize].paddingRight = inputSize === 'lg' ? '48px' : '44px';
    }

    return {
      ...baseStyles,
      ...sizeStyles[inputSize],
      ...style,
    };
  };

  const getIconStyles = (position: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: '50%',
    [position]: inputSize === 'lg' ? '16px' : '12px',
    transform: 'translateY(-50%)',
    pointerEvents: 'auto', // Allow pointer events for interactive icons
    display: 'flex',
    alignItems: 'center',
    color: hasError ? COLORS.error : COLORS.greyText,
    fontSize: '14px',
  });

  const getMessageStyles = (isError: boolean): React.CSSProperties => ({
    margin: '2px 0 0 0',
    fontSize: '13px',
    color: isError ? COLORS.error : COLORS.greyText,
    lineHeight: '1.4',
  });

  return (
    <div style={getContainerStyles()} className={className}>
      {label && (
        <label htmlFor={inputId} style={getLabelStyles()}>
          {label}
          {required && (
            <span style={{ color: COLORS.error }} aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div style={getInputWrapperStyles()}>
        {leftIcon && (
          <div style={getIconStyles('left')} aria-hidden="true">
            {leftIcon}
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          style={getInputStyles()}
          disabled={disabled}
          required={required}
          aria-invalid={hasError}
          aria-describedby={
            error ? `${inputId}-error` :
              helperText ? `${inputId}-helper` :
                undefined
          }
          {...props}
        />

        {rightIcon && (
          <div style={getIconStyles('right')} aria-hidden="true">
            {rightIcon}
          </div>
        )}
      </div>

      {error && (
        <p
          id={`${inputId}-error`}
          style={getMessageStyles(true)}
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}

      {helperText && !error && (
        <p
          id={`${inputId}-helper`}
          style={getMessageStyles(false)}
        >
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
